import { inflect } from 'inflection';
import React, { useState } from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import { Cards } from '../../components/Card';
import MetaList, { MetaListItem } from '../../components/MetaList';
import {
  PlanInterval,
  PlanTier,
  PlanType,
} from '../../graphql/generated/graphql';
import SettingCard from '../../settings/SettingCard';
import EditPlan from './EditPlan';
import { getMonthlyPrice } from './priceCalculator';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';

const INTERVAL_TEXT = {
  YEARLY: 'annually',
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly',
};

const BILLING_SUMMARY_FRAGMENT = gql(`
  fragment Plan_billingSummary on BillingSummary {
    id
    userCount
    seatCount
    plan {
      name
      tier
      interval
      type
    }
    coupon {
      ...getMonthlyPrice_billingCoupon
    }
    ...EditPlan_billingSummary
  }
`);

const BILLING_PRICING_FRAGMENT = gql(`
  fragment Plan_billingPricing on BillingPricing {
    ...EditPlan_billingPricing
  }
`);

const planTierToPlan = (planTier?: PlanTier) => {
  switch (planTier) {
    case PlanTier.Business:
      return 'business';
    case PlanTier.Startup:
      return 'startup';
    default:
      return 'free';
  }
};

interface PlanProps {
  billingSummary: DocumentType<typeof BILLING_SUMMARY_FRAGMENT>;
  billingPricing: DocumentType<typeof BILLING_PRICING_FRAGMENT>;
  updateBilling: (values: {
    email?: string;
    source?: string;
    planTier?: PlanTier;
    planInterval?: PlanInterval;
  }) => Promise<unknown>;
  initialTier: PlanTier | null;
  initialInterval: PlanInterval | null;
}

const Plan = ({
  billingSummary,
  billingPricing,
  updateBilling,
  initialTier,
  initialInterval,
}: PlanProps) => {
  const seatLimit = useAppSelector(
    (state) => state.session.organization.settings?.seatLimit,
  );

  const [isEditingPlan, setIsEditingPlan] = useState(
    Boolean(initialTier || initialInterval),
  );

  if (isEditingPlan) {
    return (
      <EditPlan
        billingSummary={billingSummary}
        initialTier={initialTier}
        initialInterval={initialInterval}
        billingPricing={billingPricing}
        onClose={() => setIsEditingPlan(false)}
        updateBilling={updateBilling}
      />
    );
  }

  const { plan, userCount, seatCount, coupon } = billingSummary;

  if (!plan) {
    return null;
  }

  const tierPricing = billingPricing[planTierToPlan(plan.tier)];
  const planPricing =
    tierPricing?.[
      plan.interval === PlanInterval.Monthly ? 'monthly' : 'yearly'
    ];

  const seatDescription = seatLimit
    ? `${seatCount} of ${seatLimit} seats used`
    : `${seatCount} ${inflect('seat', seatCount)}`;

  return (
    <Cards>
      <SettingCard
        actions={
          <Button variant="secondary" onClick={() => setIsEditingPlan(true)}>
            Edit
          </Button>
        }
        title={plan.name}
        details={
          <MetaList>
            <MetaListItem>{seatDescription}</MetaListItem>
            {plan.type === PlanType.Standard && planPricing && (
              <MetaListItem>
                {getMonthlyPrice(
                  plan.interval,
                  planPricing.amount,
                  userCount,
                  coupon ?? undefined,
                )}{' '}
                per month
              </MetaListItem>
            )}
            <MetaListItem>Billed {INTERVAL_TEXT[plan.interval]}</MetaListItem>
          </MetaList>
        }
      />
    </Cards>
  );
};

export default Plan;
